// Display settings from the user menu (language, Order Hist page, text size, colour theme and
// style) for the new-style surfaces: top nav, CS Notes, Order Hist 2 and the ffc-page views
// (In Ship, Data Load, Control Page, Settings, Shipping Tool). Older Bulma pages ignore them.
//
// They belong to the user, not the browser: saved on the server (/api/v1/user-ui-prefs/,
// logicconfig) so they follow the user to any computer. This browser keeps a copy per user only
// so a page paints in the right colours before the server answers, plus one copy of the last
// user's settings for the login screen. Applied on <html> as data
// attributes and --ff-* variables; App.vue holds the CSS that reads them.

import axios from 'axios'
import { reactive } from 'vue'
import STYLE_PRESETS from './stylePresets.json'

export { STYLE_PRESETS }

const CACHE_KEY = 'ffUiPrefs'
const SERVER_URL = '/api/v1/user-ui-prefs/'
const SAVE_DELAY = 600

export const THEMES = [
  { key: 'herb', label: 'Herb', swatch: '#1f7a4d' },
  { key: 'slate', label: 'Slate', swatch: '#2d5fa6' },
  { key: 'plum', label: 'Plum', swatch: '#5e3f96' },
]

// Colour theme a user starts with. deploy-ann.yml turns this into 'plum' (the ANN logo
// purple) for the ann build, so its pages and nav are purple out of the box; keep the line
// verbatim, the sed matches it.
export const DEFAULT_THEME = 'herb'

export const FONT_SIZES = [
  { key: 's', label: 'Small', i18n: 'sizeSmall', scale: 0.9 },
  { key: 'm', label: 'Default', i18n: 'sizeDefault', scale: 1 },
  { key: 'l', label: 'Large', i18n: 'sizeLarge', scale: 1.1 },
  { key: 'xl', label: 'Extra large', i18n: 'sizeXLarge', scale: 1.22 },
]

// 'custom' takes its brand colour from `custom` (a #rrggbb picked in the user menu);
// App.vue derives the darker ink and the hairline tone from it.
export const DEFAULT_CUSTOM = '#7a4fbf'
// orderHist: which Order Hist page /orderhistory renders (router/templateSwitch.js).
// hidden: not in the user menu, and a saved pick of it falls back to the default. Take the flag
// off to offer the page again (it and its route stay in place).
export const ORDER_HIST_VARIANTS = [
  { key: 'old', i18n: 'orderHistOld' },
  { key: 'new', i18n: 'orderHistNew' },
  { key: 'exp', i18n: 'orderHistExp', hidden: true },
  // the new page plus the Note LLM composer (OrderHistExp2View); TEMPORARILY hidden (2026-11-04):
  // drop `hidden: true` to show it again
  { key: 'exp2', i18n: 'orderHistExp2', hidden: true },
]
// nav: where the menu sits (App.vue): the sidebar on the left, or the bar across the top.
export const NAV_PLACES = [
  { key: 'side', i18n: 'navSide' },
  { key: 'top', i18n: 'navTop' },
]
// lang: language of the i18n pack (src/i18n), Vietnamese by default.
export const LANGUAGES = [
  { key: 'vi', label: 'Tiếng Việt' },
  { key: 'en', label: 'English' },
]

// Style: the page, panel, text and outline colours. Off is the standard look (white panels on a
// light grey page, App.vue :root). On is one of the presets (stylePresets.json: six hues, each
// light or calm; checked by .claude/skills/ui-comfort/scripts/contrast.mjs) or the user's own
// colours (styleCustom): changing a colour in the menu turns the current preset into a custom
// style. A style also calms motion (calmMotion, App.vue).
export const DEFAULT_STYLE = 'sand-calm'
// the four colours a user can change; muted text and dividers follow from them
export const STYLE_EDITABLE = ['paper', 'surface', 'ink', 'line']
const STYLE_VARS = ['paper', 'surface', 'ink', 'muted', 'hairline', 'line']

const HEX_RE = /^#[0-9a-f]{6}$/i
const presetOf = key => STYLE_PRESETS.find(s => s.key === key)
const editableOf = tokens => Object.fromEntries(STYLE_EDITABLE.map(k => [k, tokens[k]]))

export const DEFAULT_PREFS = {
  theme: DEFAULT_THEME,
  font: 'm',
  custom: DEFAULT_CUSTOM,
  orderHist: 'new',
  nav: 'side',
  lang: 'vi',
  styleOn: false,
  style: DEFAULT_STYLE,
  styleCustom: editableOf(presetOf(DEFAULT_STYLE)),
}

// #rrggbb `a` over `b` at weight t (what CSS color-mix in srgb does)
export function mixHex (a, b, t) {
  const [x, y] = [a, b].map(h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16)))
  return '#' + x.map((v, i) => Math.round(v * t + y[i] * (1 - t)).toString(16).padStart(2, '0')).join('')
}

// WCAG 2 contrast ratio of two #rrggbb colours (the menu shows it next to text and outlines)
export function contrastRatio (a, b) {
  const lum = hex => {
    const [r, g, bl] = [1, 3, 5].map(i => {
      const c = parseInt(hex.slice(i, i + 2), 16) / 255
      return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl
  }
  const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}

function valid (prefs) {
  const custom = HEX_RE.test(prefs.custom || '') ? prefs.custom.toLowerCase() : DEFAULT_CUSTOM
  const known = prefs.theme === 'custom' || THEMES.some(t => t.key === prefs.theme)
  const styleCustom = { ...DEFAULT_PREFS.styleCustom }
  STYLE_EDITABLE.forEach(k => {
    const v = prefs.styleCustom && prefs.styleCustom[k]
    if (HEX_RE.test(v || '')) styleCustom[k] = v.toLowerCase()
  })
  return {
    theme: known ? prefs.theme : DEFAULT_PREFS.theme,
    font: FONT_SIZES.some(f => f.key === prefs.font) ? prefs.font : DEFAULT_PREFS.font,
    custom,
    orderHist: ORDER_HIST_VARIANTS.some(v => !v.hidden && v.key === prefs.orderHist) ? prefs.orderHist : DEFAULT_PREFS.orderHist,
    nav: NAV_PLACES.some(n => n.key === prefs.nav) ? prefs.nav : DEFAULT_PREFS.nav,
    lang: LANGUAGES.some(l => l.key === prefs.lang) ? prefs.lang : DEFAULT_PREFS.lang,
    styleOn: prefs.styleOn === true,
    style: prefs.style === 'custom' || presetOf(prefs.style) ? prefs.style : DEFAULT_STYLE,
    styleCustom,
  }
}

// Settings saved before Style existed (no `styleOn`):
//  - 'herb' was only ever the default (on ann too): it becomes this build's default theme;
//  - Clay is gone: its users keep the same colour as a custom theme;
//  - Background / Surfaces / eye comfort became Style (a non-default pick turns into a style).
const OLD_BACKGROUNDS = { white: '#ffffff', stone: '#e6e4e0', mist: '#eef1f4' }
const OLD_SURFACES = { white: '#ffffff', soft: '#fdfcfb', dim: '#f9f8f6' }
const OLD_COMFORTS = { warm: 'sand-calm', sage: 'sage-calm', blue: 'mist-calm' }

function migrate (saved) {
  const p = { ...saved }
  if (p.styleOn !== undefined) return p
  if (p.theme === 'herb') p.theme = DEFAULT_THEME
  if (p.theme === 'clay') Object.assign(p, { theme: 'custom', custom: '#bc5a33' })
  const page = p.bg === 'custom' ? p.bgCustom : OLD_BACKGROUNDS[p.bg]
  if (OLD_COMFORTS[p.comfort]) {
    Object.assign(p, { styleOn: true, style: OLD_COMFORTS[p.comfort] })
  } else if ((p.bg && p.bg !== 'mist') || (p.surface && p.surface !== 'white')) {
    const styleCustom = { paper: page || '#eef1f4', surface: OLD_SURFACES[p.surface] || '#ffffff', ink: '#1d211e', line: '#8c8780' }
    Object.assign(p, { styleOn: true, style: 'custom', styleCustom })
  }
  return p
}

// The colours a setting paints with, or null when the style is off.
export function styleTokens (prefs) {
  const p = valid(prefs || {})
  if (!p.styleOn) return null
  if (p.style !== 'custom') return presetOf(p.style)
  const c = p.styleCustom
  return { ...c, muted: mixHex(c.ink, c.surface, 0.76), hairline: mixHex(c.line, c.surface, 0.45) }
}

function currentUser () {
  try { return localStorage.getItem('token') ? localStorage.getItem('user') : null } catch (error) { return null }
}

const cacheKey = user => (user ? `${CACHE_KEY}:${user}` : CACHE_KEY)

function readCache (key) {
  try { return JSON.parse(localStorage.getItem(key)) } catch (error) { return null }
}

// The user's copy, and the browser-wide one too: the login screen (nobody signed in) paints with
// it, so it looks the way the last user here left it.
function writeCache (p) {
  try {
    const json = JSON.stringify(p)
    const user = currentUser()
    if (user) localStorage.setItem(cacheKey(user), json)
    localStorage.setItem(CACHE_KEY, json)
  } catch (error) { /* private mode */ }
}

// The signed-in user's copy here; before sign-in (or the first time) the browser-wide one that
// older versions saved, which then becomes the user's on the first sync.
export function loadUiPrefs () {
  const user = currentUser()
  const saved = (user && readCache(cacheKey(user))) || readCache(CACHE_KEY) || {}
  return valid(migrate(saved))
}

export function applyUiPrefs (prefs) {
  const p = valid(prefs || {})
  const root = document.documentElement
  root.setAttribute('data-ff-theme', p.theme)
  root.setAttribute('data-ff-font', p.font)
  root.setAttribute('data-ff-nav', p.nav)
  root.setAttribute('lang', p.lang)
  // a style's colours go inline, so they win over the theme's hairline and the :root defaults
  const tokens = styleTokens(p)
  if (tokens) root.setAttribute('data-ff-style', p.style)
  else root.removeAttribute('data-ff-style')
  STYLE_VARS.forEach(k => {
    if (tokens) root.style.setProperty('--ff-' + k, tokens[k])
    else root.style.removeProperty('--ff-' + k)
  })
  if (p.theme === 'custom') root.style.setProperty('--ff-brand', p.custom)
  else root.style.removeProperty('--ff-brand')
  return p
}

let saveTimer = null

// to the server a moment after the last change (a colour picker sends many)
function queueServerSave (p) {
  if (!currentUser()) return
  clearTimeout(saveTimer)
  saveTimer = setTimeout(() => {
    axios.put(SERVER_URL, p).catch(() => { /* offline: the copy here stays; the next change retries */ })
  }, SAVE_DELAY)
}

export function saveUiPrefs (prefs) {
  const p = applyUiPrefs(prefs)
  Object.assign(uiState, p)
  writeCache(p)
  queueServerSave(p)
  return p
}

// After sign-in and when the app starts signed in: the user's settings on the server win. The
// first time (nothing saved there yet) this browser's settings become the user's.
export async function syncUiPrefsFromServer () {
  if (!currentUser()) return null
  try {
    const { data } = await axios.get(SERVER_URL)
    // saved settings hold at least one of our keys; anything else counts as nothing saved yet
    const saved = data && typeof data === 'object' && !Array.isArray(data) && Object.keys(DEFAULT_PREFS).some(k => k in data)
    if (saved) {
      const p = applyUiPrefs(migrate(data))
      Object.assign(uiState, p)
      writeCache(p)
      return p
    }
    const mine = valid({ ...uiState })
    writeCache(mine)
    await axios.put(SERVER_URL, mine)
    return mine
  } catch (error) {
    return null // keep this browser's copy
  }
}

// One live copy for the whole app: the nav, the user menu and the route switcher all
// read it, so flipping a setting re-renders everything at once.
export const uiState = reactive(applyUiPrefs(loadUiPrefs()))

// Fewer moving parts: with a style on, or when the system asks for reduced motion.
// Scripted scrolls then jump instead of gliding (App.vue stills CSS transitions the same way).
export function calmMotion () {
  const reduce = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  return uiState.styleOn || Boolean(reduce)
}
